import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * Manages gift-wrap and gift-message cart attributes.
 *
 * @typedef {object} GiftOptionRefs
 * @property {HTMLInputElement} giftBox
 * @property {HTMLInputElement} giftBoxFormValue
 * @property {HTMLTextAreaElement} giftMessage
 * @property {HTMLElement} characterCount
 * @property {HTMLElement} status
 * @property {HTMLDialogElement} dialog
 *
 * @extends {Component<GiftOptionRefs>}
 */
class CartGiftOptions extends Component {
  requiredRefs = ['giftBox', 'giftBoxFormValue', 'giftMessage', 'characterCount', 'status', 'dialog'];

  /** @type {AbortController | null} */
  #giftBoxFetch = null;

  /** @type {AbortController | null} */
  #giftMessageFetch = null;

  /** @type {boolean} */
  #persistedGiftBoxValue = false;

  /** @type {string} */
  #persistedGiftMessageValue = '';

  /** @type {boolean | null} */
  #pendingGiftBoxValue = null;

  /** @type {string | null} */
  #pendingGiftMessageValue = null;

  connectedCallback() {
    super.connectedCallback();

    this.#persistedGiftBoxValue = this.refs.giftBox.checked;
    this.#persistedGiftMessageValue = this.refs.giftMessage.value;
    this.#updateCharacterCount();
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.refs.dialog.addEventListener('keydown', this.#handleDialogKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#saveGiftMessage.cancel();
    this.#giftBoxFetch?.abort();
    this.#giftMessageFetch?.abort();
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
    this.refs.dialog.removeEventListener('keydown', this.#handleDialogKeydown);
  }

  updatedCallback() {
    super.updatedCallback();

    const giftBoxValue = this.#pendingGiftBoxValue ?? this.#persistedGiftBoxValue;
    const giftMessageValue = this.#pendingGiftMessageValue ?? this.#persistedGiftMessageValue;

    // A quantity/discount section response may have started before these
    // attribute requests completed. Keep the customer's newer local values.
    this.refs.giftBox.checked = giftBoxValue;
    this.refs.giftBoxFormValue.value = giftBoxValue ? 'true' : '';
    this.refs.giftMessage.value = giftMessageValue;

    this.#updateCharacterCount();
  }

  /**
   * Updates and persists the gift-wrap selection.
   * @param {Event} event
   */
  handleGiftBoxChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const checked = event.target.checked;
    this.#pendingGiftBoxValue = checked;
    this.refs.giftBoxFormValue.value = checked ? 'true' : '';
    this.#clearStatus();
    this.#saveGiftBox(checked, event);
  };

  /**
   * Updates the character count and schedules message persistence.
   * @param {InputEvent} event
   */
  handleGiftMessageInput = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    if (event.target.value.length > event.target.maxLength) {
      event.target.value = event.target.value.slice(0, event.target.maxLength);
    }

    this.#pendingGiftMessageValue = event.target.value;
    this.#updateCharacterCount();
    this.#clearStatus();
    this.#saveGiftMessage(event.target.value, event);
  };

  /**
   * Flushes the current message on blur/change.
   * @param {Event} event
   */
  handleGiftMessageChange = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    this.#saveGiftMessage.cancel();
    this.#pendingGiftMessageValue = event.target.value;
    this.#saveGiftMessageNow(event.target.value, event);
  };

  showGiftInfo = () => {
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
  };

  closeGiftInfo = () => {
    this.refs.dialog.close();
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    if (event.target !== this.refs.dialog) return;

    event.stopPropagation();
    this.refs.dialog.close();
  };

  /** @param {KeyboardEvent} event */
  #handleDialogKeydown = (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  };

  /**
   * @param {boolean} checked
   * @param {Event} event
   */
  async #saveGiftBox(checked, event) {
    this.#giftBoxFetch?.abort();
    const controller = new AbortController();
    this.#giftBoxFetch = controller;

    try {
      await this.#updateAttribute(this.dataset.giftBoxAttributeName ?? 'giftBox', checked ? 'true' : '', controller);
      this.#persistedGiftBoxValue = checked;
      this.refs.giftBox.defaultChecked = checked;

      if (this.#pendingGiftBoxValue === checked) this.#pendingGiftBoxValue = null;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#pendingGiftBoxValue = null;
      this.refs.giftBox.checked = this.#persistedGiftBoxValue;
      this.refs.giftBoxFormValue.value = this.#persistedGiftBoxValue ? 'true' : '';
      this.#showError();
    } finally {
      if (this.#giftBoxFetch === controller) this.#giftBoxFetch = null;
      cartPerformance.measureFromEvent('gift-box-update:user-action', event);
    }
  }

  #saveGiftMessage = debounce((value, event) => {
    this.#saveGiftMessageNow(value, event);
  }, 200);

  /**
   * @param {string} value
   * @param {Event} event
   */
  async #saveGiftMessageNow(value, event) {
    this.#giftMessageFetch?.abort();
    const controller = new AbortController();
    this.#giftMessageFetch = controller;

    try {
      await this.#updateAttribute(this.dataset.giftMessageAttributeName ?? 'giftMessage', value, controller);
      this.#persistedGiftMessageValue = value;
      this.refs.giftMessage.defaultValue = value;

      if (this.#pendingGiftMessageValue === value) this.#pendingGiftMessageValue = null;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // Keep the customer's visible text and the associated form value intact.
      this.#showError();
    } finally {
      if (this.#giftMessageFetch === controller) this.#giftMessageFetch = null;
      cartPerformance.measureFromEvent('gift-message-update:user-action', event);
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   * @param {AbortController} controller
   */
  async #updateAttribute(key, value, controller) {
    const config = fetchConfig('json', {
      body: JSON.stringify({ attributes: { [key]: value } }),
    });
    const response = await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`Cart attribute update failed: ${response.status}`);
  }

  #updateCharacterCount() {
    const current = this.refs.giftMessage.value.length.toString();
    const maximum = this.refs.giftMessage.maxLength.toString();
    const template = this.refs.characterCount.dataset.template ?? '([current]/[maximum])';

    this.refs.characterCount.textContent = template.replace('[current]', current).replace('[maximum]', maximum);
  }

  #clearStatus() {
    this.refs.status.textContent = '';
  }

  #showError() {
    this.refs.status.textContent = this.refs.status.dataset.errorMessage ?? '';
  }
}

if (!customElements.get('cart-gift-options')) {
  customElements.define('cart-gift-options', CartGiftOptions);
}
